<template>
    <div class="space-y-6">
        <!-- Welcome Section -->
        <div class="bg-gradient-to-r from-primary-600 to-primary-800 rounded-lg p-6 text-white shadow-lg relative">
            <div class="flex items-start justify-between">
                <div>
                    <h1 class="text-3xl font-bold">Welcome back, {{ user?.first_name }}!</h1>
                    <p class="mt-2 text-primary-100">{{ currentDate }}</p>
                    <!-- User Roles Badge -->
                    <div v-if="userRoles && userRoles.length > 0" class="mt-3 flex flex-wrap gap-2">
                        <span
                            v-for="role in userRoles"
                            :key="role"
                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white/20 text-white backdrop-blur"
                        >
                            {{ role }}
                        </span>
                    </div>
                </div>
                <button
                    @click="showInfo = true"
                    class="p-2 text-white/80 hover:text-white rounded-full hover:bg-white/10 transition-colors"
                    aria-label="Dashboard information"
                >
                    <InformationCircleIcon class="h-6 w-6" />
                </button>
            </div>

            <!-- 2FA Status Badge (Bottom Right) -->
            <div v-if="!user?.two_fa_enabled" class="absolute bottom-4 right-4">
                <Link
                    href="/profile"
                    class="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold bg-red-500 hover:bg-red-600 text-white shadow-lg transition-colors cursor-pointer"
                    title="Click to enable 2FA"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    2FA Not Enabled
                </Link>
            </div>

            <!-- Trial Notice -->
            <div v-if="subscription?.is_trial" class="mt-4 bg-white/20 backdrop-blur rounded-lg p-4">
                <div class="flex items-center justify-between">
                    <div>
                        <p class="font-medium">Trial Period</p>
                        <p class="text-sm text-primary-100">{{ subscription.trial_days_remaining }} days remaining</p>
                    </div>
                    <Link :href="route('settings.billing.index')" class="bg-white text-primary-600 px-4 py-2 rounded-md font-semibold hover:bg-primary-50 transition-colors">
                        Upgrade Now
                    </Link>
                </div>
            </div>
        </div>

        <!-- Manager Stats (Role-Based) -->
        <div v-if="hasManagerStats" class="grid gap-6" :class="managerStatsGridClass">
            <!-- Tickets Manager Card -->
            <ManagerStatCard
                v-if="managerStats.tickets"
                title="Tickets"
                :total="managerStats.tickets.total_open"
                :breakdown="managerStats.tickets.breakdown"
                icon="TicketIcon"
                color="blue"
                href="/tickets"
            />

            <!-- Work Orders Manager Card -->
            <ManagerStatCard
                v-if="managerStats.work_orders"
                title="Work Orders"
                :total="managerStats.work_orders.total_open"
                :breakdown="managerStats.work_orders.breakdown"
                icon="WrenchIcon"
                color="green"
                href="/work-orders"
            />

            <!-- Incidents Manager Card -->
            <ManagerStatCard
                v-if="managerStats.incidents"
                title="Incidents"
                :total="managerStats.incidents.total_open"
                :breakdown="managerStats.incidents.breakdown"
                icon="ExclamationTriangleIcon"
                color="red"
                href="/incidents"
            />
        </div>

        <!-- Personal Stats (All Users) -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <!-- IT Helpdesk Module -->
            <PersonalModuleCard
                v-if="hasModule('it_helpdesk')"
                title="IT Helpdesk"
                icon="TicketIcon"
                color="blue"
                :stats="{
                    total: personalStats.tickets?.total_raised || 0,
                    inProgress: personalStats.tickets?.in_progress || 0,
                }"
                :links="{
                    viewAll: '/tickets',
                    create: '/tickets/create'
                }"
            />

            <!-- Maintenance Module -->
            <PersonalModuleCard
                v-if="hasModule('maintenance')"
                title="Maintenance"
                icon="WrenchIcon"
                color="green"
                :stats="{
                    total: personalStats.work_orders?.total_raised || 0,
                    inProgress: personalStats.work_orders?.in_progress || 0,
                }"
                :links="{
                    viewAll: '/work-orders',
                    create: '/work-orders/create'
                }"
            />

            <!-- Incident Management Module -->
            <PersonalModuleCard
                v-if="hasModule('incidents')"
                title="Incident Management"
                icon="ExclamationTriangleIcon"
                color="red"
                :stats="{
                    total: personalStats.incidents?.total_raised || 0,
                    inProgress: personalStats.incidents?.in_progress || 0,
                }"
                :links="{
                    viewAll: '/incidents',
                    create: '/incidents/create'
                }"
            />
        </div>

        <!-- SLA Performance Widgets -->
        <div v-if="(helpdeskSla && helpdeskSla.total_active > 0) || (maintenanceSla && maintenanceSla.total_active > 0)" class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- IT Helpdesk SLA Widget -->
            <div v-if="helpdeskSla && helpdeskSla.total_active > 0" class="bg-white dark:bg-secondary-800 shadow rounded-lg border border-secondary-200 dark:border-secondary-700">
                <div class="px-6 py-5 border-b border-secondary-200 dark:border-secondary-700">
                    <div class="flex items-center justify-between">
                        <div>
                            <h3 class="text-lg leading-6 font-medium text-secondary-900 dark:text-white">
                                IT Helpdesk SLA Performance
                            </h3>
                            <p class="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
                                Active tickets with SLA tracking
                            </p>
                        </div>
                    </div>
                </div>
                <div class="p-6">
                    <div class="grid grid-cols-2 gap-4">
                        <!-- Breached -->
                        <div class="text-center">
                            <div class="flex items-center justify-center w-12 h-12 mx-auto rounded-full bg-red-100 dark:bg-red-900/30">
                                <svg class="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <p class="mt-2 text-3xl font-bold text-red-600 dark:text-red-400">{{ helpdeskSla.breached_count }}</p>
                            <p class="mt-1 text-sm text-secondary-500 dark:text-secondary-400">Breached</p>
                        </div>

                        <!-- At Risk -->
                        <div class="text-center">
                            <div class="flex items-center justify-center w-12 h-12 mx-auto rounded-full bg-yellow-100 dark:bg-yellow-900/30">
                                <svg class="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <p class="mt-2 text-3xl font-bold text-yellow-600 dark:text-yellow-400">{{ helpdeskSla.at_risk_count }}</p>
                            <p class="mt-1 text-sm text-secondary-500 dark:text-secondary-400">At Risk</p>
                        </div>

                        <!-- On Track -->
                        <div class="text-center">
                            <div class="flex items-center justify-center w-12 h-12 mx-auto rounded-full bg-green-100 dark:bg-green-900/30">
                                <svg class="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <p class="mt-2 text-3xl font-bold text-green-600 dark:text-green-400">{{ helpdeskSla.on_track_count }}</p>
                            <p class="mt-1 text-sm text-secondary-500 dark:text-secondary-400">On Track</p>
                        </div>

                        <!-- Performance Rate -->
                        <div class="text-center">
                            <div class="flex items-center justify-center w-12 h-12 mx-auto rounded-full bg-blue-100 dark:bg-blue-900/30">
                                <svg class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                            </div>
                            <p class="mt-2 text-3xl font-bold text-blue-600 dark:text-blue-400">{{ helpdeskSla.performance_rate }}%</p>
                            <p class="mt-1 text-sm text-secondary-500 dark:text-secondary-400">30-Day Performance</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Maintenance SLA Widget -->
            <div v-if="maintenanceSla && maintenanceSla.total_active > 0" class="bg-white dark:bg-secondary-800 shadow rounded-lg border border-secondary-200 dark:border-secondary-700">
                <div class="px-6 py-5 border-b border-secondary-200 dark:border-secondary-700">
                    <div class="flex items-center justify-between">
                        <div>
                            <h3 class="text-lg leading-6 font-medium text-secondary-900 dark:text-white">
                                Maintenance SLA Performance
                            </h3>
                            <p class="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
                                Active work orders with SLA tracking
                            </p>
                        </div>
                        <Link
                            :href="route('maintenance.sla-dashboard')"
                            class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-primary-700 dark:text-primary-300 bg-primary-100 dark:bg-primary-900/30 hover:bg-primary-200 dark:hover:bg-primary-900/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                        >
                            View Details
                        </Link>
                    </div>
                </div>
                <div class="p-6">
                    <div class="grid grid-cols-2 gap-4">
                        <!-- Breached -->
                        <div class="text-center">
                            <div class="flex items-center justify-center w-12 h-12 mx-auto rounded-full bg-red-100 dark:bg-red-900/30">
                                <svg class="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <p class="mt-2 text-3xl font-bold text-red-600 dark:text-red-400">{{ maintenanceSla.breached_count }}</p>
                            <p class="mt-1 text-sm text-secondary-500 dark:text-secondary-400">Breached</p>
                        </div>

                        <!-- At Risk -->
                        <div class="text-center">
                            <div class="flex items-center justify-center w-12 h-12 mx-auto rounded-full bg-yellow-100 dark:bg-yellow-900/30">
                                <svg class="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <p class="mt-2 text-3xl font-bold text-yellow-600 dark:text-yellow-400">{{ maintenanceSla.at_risk_count }}</p>
                            <p class="mt-1 text-sm text-secondary-500 dark:text-secondary-400">At Risk</p>
                        </div>

                        <!-- On Track -->
                        <div class="text-center">
                            <div class="flex items-center justify-center w-12 h-12 mx-auto rounded-full bg-green-100 dark:bg-green-900/30">
                                <svg class="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <p class="mt-2 text-3xl font-bold text-green-600 dark:text-green-400">{{ maintenanceSla.on_track_count }}</p>
                            <p class="mt-1 text-sm text-secondary-500 dark:text-secondary-400">On Track</p>
                        </div>

                        <!-- Performance Rate -->
                        <div class="text-center">
                            <div class="flex items-center justify-center w-12 h-12 mx-auto rounded-full bg-blue-100 dark:bg-blue-900/30">
                                <svg class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                            </div>
                            <p class="mt-2 text-3xl font-bold text-blue-600 dark:text-blue-400">{{ maintenanceSla.performance_rate }}%</p>
                            <p class="mt-1 text-sm text-secondary-500 dark:text-secondary-400">30-Day Performance</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Activity Feed & My Tasks -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <!-- Recent Activity (2 columns) -->
            <div class="lg:col-span-2 bg-white dark:bg-secondary-800 shadow rounded-lg border border-secondary-200 dark:border-secondary-700">
                <div class="px-6 py-5 border-b border-secondary-200 dark:border-secondary-700">
                    <h3 class="text-lg leading-6 font-medium text-secondary-900 dark:text-white">My Recent Activity</h3>
                    <p class="mt-1 text-sm text-secondary-500 dark:text-secondary-400">Your latest actions across all modules</p>
                </div>
                <div class="p-6">
                    <div class="flex space-x-2 border-b border-secondary-200 dark:border-secondary-700 pb-2 mb-4">
                        <button
                            v-for="module in activityModules"
                            :key="module.value"
                            @click="activityFilter = module.value"
                            :class="[
                                'px-3 py-1 rounded text-sm font-medium transition-colors',
                                activityFilter === module.value
                                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                                    : 'text-secondary-600 dark:text-secondary-400 hover:bg-secondary-100 dark:hover:bg-secondary-700'
                            ]"
                        >
                            {{ module.label }}
                        </button>
                    </div>

                    <div class="space-y-3 max-h-96 overflow-y-auto scrollbar-thin">
                        <div
                            v-for="activity in filteredActivity"
                            :key="activity.id"
                            class="flex items-start space-x-3 p-3 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700/50 transition-colors"
                        >
                            <div class="flex-shrink-0">
                                <div :class="[
                                    'w-8 h-8 rounded-full flex items-center justify-center',
                                    getActivityIconBg(activity.module)
                                ]">
                                    <component :is="getActivityIcon(activity.module)" class="w-4 h-4" :class="getActivityIconColor(activity.module)" />
                                </div>
                            </div>
                            <div class="flex-1 min-w-0">
                                <p class="text-sm text-secondary-900 dark:text-white">
                                    {{ activity.description }}
                                </p>
                                <p class="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                                    {{ activity.ticket_number || activity.work_order_number || activity.incident_number }} • {{ activity.timestamp }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div v-if="filteredActivity.length === 0" class="text-center py-8 text-secondary-500 dark:text-secondary-400">
                        <InboxIcon class="h-12 w-12 mx-auto mb-2 text-secondary-400 dark:text-secondary-500" />
                        <p>No recent activity</p>
                    </div>
                </div>
            </div>

            <!-- My Tasks (1 column) -->
            <div class="bg-white dark:bg-secondary-800 shadow rounded-lg border border-secondary-200 dark:border-secondary-700">
                <div class="px-6 py-5 border-b border-secondary-200 dark:border-secondary-700">
                    <h3 class="text-lg leading-6 font-medium text-secondary-900 dark:text-white">My Tasks</h3>
                    <p class="mt-1 text-sm text-secondary-500 dark:text-secondary-400">Tickets, work orders, incidents, and checklists assigned to you</p>
                </div>
                <div class="p-6">
                    <div class="space-y-3 max-h-96 overflow-y-auto scrollbar-thin">
                        <Link
                            v-for="task in myTasks"
                            :key="task.id"
                            :href="task.url"
                            class="block p-3 rounded-lg border border-secondary-200 dark:border-secondary-600 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-sm transition-all"
                        >
                            <div class="flex items-start justify-between">
                                <div class="flex-1 min-w-0">
                                    <p class="text-sm font-medium text-secondary-900 dark:text-white truncate">
                                        {{ task.title }}
                                    </p>
                                    <p class="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                                        {{ task.number }}
                                    </p>
                                </div>
                                <span :class="[
                                    'ml-2 px-2 py-1 text-xs font-medium rounded',
                                    getStatusBadgeClass(task.status)
                                ]">
                                    {{ task.status_label }}
                                </span>
                            </div>
                            <div v-if="task.due_date" class="mt-2 flex items-center text-xs text-secondary-500 dark:text-secondary-400">
                                <CalendarIcon class="w-4 h-4 mr-1" />
                                {{ task.due_date }}
                            </div>
                        </Link>
                    </div>

                    <div v-if="myTasks.length === 0" class="text-center py-8 text-secondary-500 dark:text-secondary-400">
                        <CheckCircleIcon class="h-12 w-12 mx-auto mb-2 text-success-400" />
                        <p class="font-medium">All caught up!</p>
                        <p class="text-sm">No pending tasks</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Info Modal -->
        <Modal :show="showInfo" @close="showInfo = false" title="Dashboard Overview" size="lg">
            <div class="space-y-4 text-sm text-secondary-600 dark:text-secondary-400">
                <p>
                    <strong class="text-secondary-900 dark:text-white">What is this page?</strong><br>
                    Your dashboard provides a comprehensive overview of your work across all modules.
                </p>
                <p>
                    <strong class="text-secondary-900 dark:text-white">Manager Stats:</strong><br>
                    If you have manager permissions, you'll see organization-wide statistics at the top showing total open items and detailed breakdowns.
                </p>
                <p>
                    <strong class="text-secondary-900 dark:text-white">Personal Stats:</strong><br>
                    View your personal statistics showing items you've created and their current status.
                </p>
                <p>
                    <strong class="text-secondary-900 dark:text-white">My Tasks:</strong><br>
                    Quick access to all items assigned to you across tickets, work orders, incidents, and checklists.
                </p>
                <p>
                    <strong class="text-secondary-900 dark:text-white">Recent Activity:</strong><br>
                    See all your recent actions and updates across the system.
                </p>
            </div>
        </Modal>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Link, usePage } from '@inertiajs/vue3';
import ManagerStatCard from '@/Components/Dashboard/ManagerStatCard.vue';
import PersonalModuleCard from '@/Components/Dashboard/PersonalModuleCard.vue';
import Modal from '@/Components/Common/Modal.vue';
import route from '@/Utils/route';
import {
    InformationCircleIcon,
    CheckCircleIcon,
    InboxIcon,
    TicketIcon,
    WrenchIcon,
    ExclamationTriangleIcon,
    CalendarIcon,
} from '@heroicons/vue/24/outline';

const props = defineProps({
    user: {
        type: Object,
        required: true,
    },
    company: {
        type: Object,
        default: null,
    },
    subscription: {
        type: Object,
        default: null,
    },
    userRoles: {
        type: Array,
        default: () => [],
    },
    managerStats: {
        type: Object,
        default: () => ({}),
    },
    personalStats: {
        type: Object,
        default: () => ({}),
    },
    maintenanceSla: {
        type: Object,
        default: null,
    },
    helpdeskSla: {
        type: Object,
        default: null,
    },
    recentActivity: {
        type: Array,
        default: () => [],
    },
    myTasks: {
        type: Array,
        default: () => [],
    },
});

// Access Inertia page props
const page = usePage();

const showInfo = ref(false);
const activityFilter = ref('all');

const activityModules = [
    { label: 'All', value: 'all' },
    { label: 'Tickets', value: 'tickets' },
    { label: 'Work Orders', value: 'work_orders' },
    { label: 'Incidents', value: 'incidents' },
];

const currentDate = computed(() => {
    return new Date().toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
});

// Get active modules from shared Inertia props
const activeModules = computed(() => page.props.activeModules || []);

const hasModule = (module) => {
    return activeModules.value.includes(module);
};

// Check if user has any manager stats
const hasManagerStats = computed(() => {
    return props.managerStats.tickets || props.managerStats.work_orders || props.managerStats.incidents;
});

// Calculate grid class based on number of manager stats cards
const managerStatsGridClass = computed(() => {
    const count = [
        props.managerStats.tickets,
        props.managerStats.work_orders,
        props.managerStats.incidents
    ].filter(Boolean).length;

    if (count === 1) return 'grid-cols-1';
    if (count === 2) return 'grid-cols-1 md:grid-cols-2';
    return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
});

const filteredActivity = computed(() => {
    if (activityFilter.value === 'all') {
        return props.recentActivity;
    }
    return props.recentActivity.filter(a => a.module === activityFilter.value);
});

function getActivityIcon(module) {
    const icons = {
        'tickets': TicketIcon,
        'work_orders': WrenchIcon,
        'incidents': ExclamationTriangleIcon,
    };
    return icons[module] || TicketIcon;
}

function getActivityIconBg(module) {
    const backgrounds = {
        'tickets': 'bg-blue-100 dark:bg-blue-900/30',
        'work_orders': 'bg-green-100 dark:bg-green-900/30',
        'incidents': 'bg-red-100 dark:bg-red-900/30',
    };
    return backgrounds[module] || 'bg-secondary-100 dark:bg-secondary-700';
}

function getActivityIconColor(module) {
    const colors = {
        'tickets': 'text-blue-600 dark:text-blue-400',
        'work_orders': 'text-green-600 dark:text-green-400',
        'incidents': 'text-red-600 dark:text-red-400',
    };
    return colors[module] || 'text-secondary-600 dark:text-secondary-400';
}

function getStatusBadgeClass(status) {
    const classes = {
        'open': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
        'in_progress': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
        'on_hold': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
        'new': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
        'acknowledged': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
        'investigating': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
        'resolved': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    };
    return classes[status] || 'bg-secondary-100 text-secondary-800 dark:bg-secondary-700 dark:text-secondary-300';
}

</script>

<style scoped>
/* Custom scrollbar */
.scrollbar-thin {
    scrollbar-width: thin;
    scrollbar-color: #9ca3af #e5e7eb;
}

.scrollbar-thin::-webkit-scrollbar {
    width: 6px;
}

.scrollbar-thin::-webkit-scrollbar-track {
    background: #e5e7eb;
    border-radius: 3px;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 3px;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
}

.dark .scrollbar-thin {
    scrollbar-color: #4b5563 #1f2937;
}

.dark .scrollbar-thin::-webkit-scrollbar-track {
    background: #1f2937;
}

.dark .scrollbar-thin::-webkit-scrollbar-thumb {
    background: #4b5563;
}

.dark .scrollbar-thin::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
}
</style>
